import type { PortalWidget, PortalWidgetStatus } from "@entrilia/shared/api/portalWidgetsTypes";
import type { UpdatePortalWidgetRequest } from "../../../../api/types/investorPortalTypes";
import { getWidgetDataSourceType } from "./portalWidgetsHelper";

export interface EditPortalWidgetState {
  name: string;
  reportId?: string;
  status: PortalWidgetStatus;
  includeRowDetails?: boolean;
  isReportRequired: boolean;
  footnote: string;
  isFootnoteShown: boolean;
}

interface ValidationErrors {
  reportId?: string;
}

export const getInitialEditPortalWidgetState = (portalWidget: PortalWidget): EditPortalWidgetState => ({
  name: portalWidget.name,
  reportId: portalWidget.reportId,
  status: portalWidget.status,
  includeRowDetails: portalWidget.includeRowDetails,
  isReportRequired: getWidgetDataSourceType(portalWidget) === "SystemReport",
  footnote: portalWidget.footnote ?? "",
  isFootnoteShown: !!portalWidget.footnote,
});

const getEffectiveFootnote = (state: EditPortalWidgetState): string =>
  state.isFootnoteShown ? state.footnote.trim() : "";

export const isEditPortalWidgetStateDirty = (state: EditPortalWidgetState, originalWidget: PortalWidget): boolean =>
  state.name.trim() !== originalWidget.name.trim() ||
  state.reportId !== originalWidget.reportId ||
  state.status !== originalWidget.status ||
  (state.includeRowDetails ?? false) !== (originalWidget.includeRowDetails ?? false) ||
  getEffectiveFootnote(state) !== (originalWidget.footnote ?? "");

export const validateEditPortalWidgetState = (
  state: EditPortalWidgetState
): [isValid: boolean, errors: ValidationErrors] => {
  if (state.isReportRequired && state.status === "Live" && !state.reportId) {
    return [false, { reportId: "A report must be selected to show this widget on the Investor Portal." }];
  }

  return [true, {}];
};

export const mapStateToUpdatePortalWidgetRequest = (
  state: EditPortalWidgetState,
  originalWidget: PortalWidget
): UpdatePortalWidgetRequest => {
  const name = state.name.trim();
  const footnote = getEffectiveFootnote(state);

  return {
    name: name === originalWidget.name.trim() ? undefined : name,
    reportId: state.reportId,
    status: state.status,
    includeRowDetails: state.includeRowDetails,
    footnoteUserInput: footnote === (originalWidget.footnote ?? "") ? undefined : footnote,
  };
};
